import { Component, Input, computed, inject, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IphonePreviewComponent } from '../../../../../components/messages/iphone-preview/iphone-preview.component';
import { StatusComponent } from '../../../../../components/ui/status/status.component';
import { SendTestMessageModalComponent } from '../../../../../components/forms/send-test-message-modal/send-test-message-modal.component';
import { SmartBroadcastService } from '../../../../../services/smart-broadcast/smart-broadcast.service';
import { exactParts } from '../../../../../services/smart-broadcast/smart-broadcast.utils';
import { ISmartBroadcastRendered } from '../../../../../types/smartBroadcast';
import { formatPhoneNumberForDisplay } from '../../../../../../utils/utils';
import { describeChar, formatCount } from '../../../../../../utils/sms';

const NOTHING: ISmartBroadcastRendered = { text: '', empty: [], unknown: [] };

/**
 * Step through the rows and see the exact SMS each person gets, worked out
 * the way the server does it. Shared by the Message and Review steps; the
 * current row lives in the store so both show the same one.
 */
@Component({
  selector: 'sb-row-preview',
  standalone: true,
  imports: [IphonePreviewComponent, StatusComponent, MatTooltipModule, SendTestMessageModalComponent],
  templateUrl: './row-preview.component.html',
  styleUrls: ['../../sb-shared.css', './row-preview.component.css'],
})
export class RowPreviewComponent {
  /** Offer "Send this row as a test". */
  @Input() testable = false;
  @Input() heading = 'Preview';

  store = inject(SmartBroadcastService);

  readonly query = signal('');
  readonly searchMiss = signal(false);
  testOpen = false;
  readonly formatCount = formatCount;

  readonly total = computed(() => this.store.rowsSig().length);

  readonly index = computed(() => {
    const total = this.total();
    return total ? Math.min(Math.max(this.store.previewRow(), 0), total - 1) : 0;
  });

  /** Live: follows the message as it is typed. */
  readonly rendered = computed(() => (this.total() ? this.store.renderRow(this.index()) : NOTHING));
  readonly parts = computed(() => exactParts(this.rendered().text));
  readonly hasBody = computed(() => !!this.store.bodySig().trim());
  readonly literal = computed(() => [...this.rendered().empty, ...this.rendered().unknown]);

  /** "130 of 160 characters": exact, for this row as sent. */
  readonly lengthLabel = computed(() => {
    const { length, parts, perPart } = this.parts();
    return parts <= 1 ? `${formatCount(length)} of ${perPart} characters` : `${formatCount(length)} characters`;
  });

  readonly partsLabel = computed(() => (this.parts().parts === 1 ? '1 part' : `${this.parts().parts} parts`));

  /** Only Unicode needs a note: it is the case that quietly raises cost. */
  readonly encodingLabel = computed(() => {
    const { encoding, offender } = this.parts();
    if (encoding !== 'Unicode') return '';
    return offender ? `Unicode, because of ${describeChar(offender)}` : 'Unicode';
  });

  /** On the phone's screen until there is a message to show. */
  readonly emptyText = computed(() =>
    this.total()
      ? `Write a message to see exactly what row ${this.sheetRow()} gets.`
      : 'Upload a sheet to preview each person’s message.'
  );

  readonly sheetRow = computed(() => this.store.rowNumbers()[this.index()] ?? this.index() + 2);

  readonly numberLabel = computed(() => {
    const phone = this.store.phones()[this.index()];
    if (!phone || !phone.raw) return 'No number';
    return phone.state === 'valid' ? formatPhoneNumberForDisplay(phone.number) : phone.raw;
  });

  /** Will this row get the message, and if not, why. */
  readonly status = computed<{ text: string; tone: string }>(() => {
    const row = this.index();
    const phone = this.store.phones()[row];
    if (this.store.phoneIndex() < 0 || !phone) return { text: 'No phone column chosen', tone: 'disabled' };
    if (this.store.isSending(row)) return { text: 'Gets this message', tone: 'sent' };
    if (phone.state === 'empty') return { text: 'Skipped: no number', tone: 'disabled' };
    if (phone.state === 'shortened') return { text: 'Left out: shortened number', tone: 'failed' };
    if (phone.state === 'invalid') return { text: 'Left out: invalid number', tone: 'failed' };
    if (phone.repeatOf >= 0) {
      const first = this.store.rowNumbers()[phone.repeatOf] ?? phone.repeatOf + 2;
      return { text: `Left out: same number as row ${first}`, tone: 'disabled' };
    }
    return { text: 'Not sent', tone: 'disabled' };
  });

  readonly checkRows = computed(() => this.store.problems().map((problem) => problem.row));

  readonly canTest = computed(() => this.hasBody() && !!this.store.senderIdSig() && this.total() > 0);

  token(field: string): string {
    return `{{${field}}}`;
  }

  literalLabel(): string {
    return this.literal().map((field) => this.token(field)).join(', ');
  }

  step(by: number) {
    const next = this.index() + by;
    if (next < 0 || next >= this.total()) return;
    this.searchMiss.set(false);
    this.store.previewRow.set(next);
  }

  /** The next row on the Rows to check list, wrapping to the first. */
  nextToCheck() {
    const rows = this.checkRows();
    if (!rows.length) return;
    const current = this.index();
    this.store.previewRow.set(rows.find((row) => row > current) ?? rows[0]);
  }

  onQuery(event: Event) {
    this.query.set((event.target as HTMLInputElement).value);
    this.searchMiss.set(false);
  }

  /** Jump to the next row containing the search text (any column). */
  find(event: Event) {
    event.preventDefault();
    const term = this.query().trim().toLowerCase();
    if (!term) return;
    const digits = term.replace(/[^\d]/g, '');
    const rows = this.store.rowsSig();
    const phones = this.store.phones();
    const total = rows.length;
    for (let step = 1; step <= total; step++) {
      const row = (this.index() + step) % total;
      const inCells = rows[row].some((cell) => cell.toLowerCase().includes(term));
      const inNumber = digits.length >= 3 && (phones[row]?.number ?? '').includes(digits);
      if (inCells || inNumber) {
        this.store.previewRow.set(row);
        this.searchMiss.set(false);
        return;
      }
    }
    this.searchMiss.set(true);
  }

  openTest() {
    if (this.canTest()) this.testOpen = true;
  }
}
